import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { TOOLTIP_DEFAULT_OPTIONS, installInstantKeyboardTooltips } from './shared/utils/tooltip-timing.util';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateCompiler } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';

import { getApp, provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { initializeAppCheck, ReCaptchaV3Provider, provideAppCheck } from '@angular/fire/app-check';
import { MtxDateFnsDatetimeModule } from '@ng-matero/extensions-date-fns-adapter';
import { MTX_DATETIME_FORMATS } from '@ng-matero/extensions/core';

import { environment } from 'src/environments/environment';

import { CommonModule, registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';

registerLocaleData(localeNl);
registerLocaleData(localeDe);
registerLocaleData(localeFr);
import { RouterModule } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { AppRoutingModule } from './app-routing.module';
import { GraphQLModule } from './graphql.module';
import { CoreModule } from './core.module';

import { AppComponent } from './app.component';
import { ShellRailComponent } from './navigation/shell-rail/shell-rail.component';
import { ShellTopbarComponent } from './navigation/shell-topbar/shell-topbar.component';
import { ToastStackComponent } from './shared/components/toast/toast-stack.component';
import { ProductionWarningBannerComponent } from './navigation/production-warning-banner/production-warning-banner.component';

import { MatCoreMaterialModule, MatLayoutMaterialModule, MatDialogMaterialModule } from './angular-material.module';
import { HasRoleDirective } from './shared/directives/has-role.directive';
import { HasAccessDirective } from './shared/directives/has-access.directive';
import { HasOwnerDirective } from './shared/directives/has-owner.directive';
import { localizedStringPipe } from './shared/pipes/localazed-string.pipe';
import * as fromLocalStorage from './shared/store/reducers';
import { AuthEffects } from './auth/store/auth.effects';

import { AddEditAddressComponent } from './shared/components/add-edit-address/add-edit-address.component';

import * as fromApp from './store/app.reducer';
import { CustomLanguagesEffects } from './store/custom-languages-store/custom-languages.effects';
import { ChannelsEffects } from './store/channels-store/channels.effects';
import { AppInitializationService } from './services/app-initialization.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatePaginatorIntl } from './shared/services/translate-paginator-intl.service';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { TranslateDatepickerIntl } from './shared/services/translate-datepicker-intl.service';


const metaReducers: Array<MetaReducer<any, any>> = [
  fromLocalStorage.localStorageSyncReducer,
  fromApp.resetAppStateOnLogout
];

// Factory function for app initialization
export function initializeAppFactory(appInitService: AppInitializationService): () => Promise<void> {
  return () => {
    return appInitService.initializeApp();
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    RouterModule,
    AppRoutingModule,
    CoreModule,
    BrowserAnimationsModule,
    GraphQLModule,
    DragDropModule,
    MatCoreMaterialModule,
    MatLayoutMaterialModule,
    MatDialogMaterialModule,
    HasRoleDirective,
    HasAccessDirective,
    HasOwnerDirective,
    localizedStringPipe,
    TranslateModule.forRoot({
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    }),
    StoreModule.forRoot(fromApp.appReducer, { metaReducers }),
    EffectsModule.forRoot([
      AuthEffects,
      CustomLanguagesEffects,
      ChannelsEffects,
    ]),
    StoreDevtoolsModule.instrument({ logOnly: environment.production }),
    MtxDateFnsDatetimeModule,
    AddEditAddressComponent,
    ProductionWarningBannerComponent,
    ShellRailComponent,
    ShellTopbarComponent,
    ToastStackComponent
  ],
  providers: [
    {
      provide: MTX_DATETIME_FORMATS,
      useValue: {
        parse: {
          dateInput: 'd MMM yyyy',
          monthInput: 'MMM',
          yearInput: 'yyyy',
          timeInput: 'h:mm aa',
          datetimeInput: 'd MMM yyyy, h:mm aa'
        },
        display: {
          dateInput: 'd MMM yyyy',
          monthInput: 'MMM',
          yearInput: 'yyyy',
          timeInput: 'h:mm aa',
          datetimeInput: 'd MMM yyyy, h:mm aa',
          monthYearLabel: 'MMM yyyy',
          dateA11yLabel: 'MMMM d, yyyy',
          monthYearA11yLabel: 'MMMM yyyy',
          popupHeaderDateLabel: 'eee, MMM d'
        }
      }
    },
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    // The control vocabulary's tooltip timing, app-wide (canon §3m; NBO-2420): 400ms on hover,
    // instant on keyboard focus — see tooltip-timing.util.ts.
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: TOOLTIP_DEFAULT_OPTIONS },
    provideAppInitializer(() => installInstantKeyboardTooltips()),
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(inject(AppInitializationService));
      return initializerFn();
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase, '[DEFAULT]')),
    provideAppCheck(() => {
      try {
        if (!environment.production) {
          (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheckDebugToken;
        }

        const appCheckInstance = initializeAppCheck(getApp('[DEFAULT]'), {
          provider: new ReCaptchaV3Provider(environment.recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true
        });

        return appCheckInstance;
      } catch (error) {
        console.error('[provideAppCheck] Error initializing Firebase App Check');
        console.error(error);
      }
    }),
    provideAuth(() => {
      const auth = getAuth(getApp('[DEFAULT]'));
      auth.tenantId = environment.firebase.tenantId;
      return auth;
    }),
    { provide: MatPaginatorIntl, useClass: TranslatePaginatorIntl },
    { provide: MatDatepickerIntl, useClass: TranslateDatepickerIntl }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(overlayContainer: OverlayContainer) {
    overlayContainer.getContainerElement().classList.add('theme-default');
  }
}
